import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  FiArrowDownRight,
  FiArrowUpRight,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiFileText,
  FiTrendingUp,
  FiUsers
} from 'react-icons/fi';
import { useApp } from '../context/AppContext';
import {
  formatCurrency,
  getFinanceTotals,
  getInvoiceRevenue,
  getInvoiceRecordDate,
  getServiceTypeLabel,
  getStatusBadgeClass,
  getStatusLabel,
  toMoney
} from '../utils/helpers';
import {
  PERIODS,
  buildChartPoints,
  getChangePercent,
  getPeriodBounds,
  getPeriodLabel,
  buildSnapshotCards,
  inPeriod,
  inPeriodInvoices,
  shiftAnchor
} from '../utils/period';
import { EXPENSE_CATEGORIES } from '../utils/constants';

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-slate-900/95 text-white px-3 py-2 shadow-xl text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((item) => (
        <p key={item.dataKey} style={{ color: item.color }}>
          {item.name}: {formatCurrency(item.value)}
        </p>
      ))}
    </div>
  );
};

const Trend = ({ value }) => {
  const up = value >= 0;
  const Icon = up ? FiArrowUpRight : FiArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-emerald-300' : 'text-rose-300'}`}>
      <Icon />
      {Math.abs(value).toFixed(0)}%
    </span>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { quotations, invoices, expenses, employees, loadingQuotations } = useApp();
  const [range, setRange] = useState('month');
  const [anchor, setAnchor] = useState(() => new Date());

  const bounds = useMemo(() => getPeriodBounds(range, anchor), [range, anchor]);
  const previousBounds = useMemo(() => getPeriodBounds(range, shiftAnchor(range, anchor, -1)), [range, anchor]);

  const periodInvoices = useMemo(() => {
    const list = inPeriodInvoices(invoices, bounds.start, bounds.end);
    return [...list].sort((a, b) => {
      const da = getInvoiceRecordDate(a)?.getTime() || 0;
      const db = getInvoiceRecordDate(b)?.getTime() || 0;
      return db - da;
    });
  }, [invoices, bounds]);
  const periodExpenses = useMemo(() => inPeriod(expenses, bounds.start, bounds.end), [expenses, bounds]);
  const periodQuotations = useMemo(() => inPeriod(quotations, bounds.start, bounds.end), [quotations, bounds]);

  const finance = useMemo(
    () => getFinanceTotals(periodInvoices, periodExpenses),
    [periodInvoices, periodExpenses]
  );
  const previousFinance = useMemo(
    () => getFinanceTotals(
      inPeriodInvoices(invoices, previousBounds.start, previousBounds.end),
      inPeriod(expenses, previousBounds.start, previousBounds.end)
    ),
    [invoices, expenses, previousBounds]
  );

  const chartPoints = useMemo(
    () => buildChartPoints(range, bounds.start, bounds.end, invoices, expenses),
    [range, bounds, invoices, expenses]
  );

  const pendingCount = periodQuotations.filter((q) => q.status === 'pending').length;
  const customers = new Set(periodInvoices.map((inv) => inv.phone).filter(Boolean)).size;
  const avgInvoice = periodInvoices.length ? finance.totalRevenue / periodInvoices.length : 0;
  const periodLabel = getPeriodLabel(range, anchor);

  const kpis = [
    {
      label: 'Dakhliga',
      hint: 'Lacagta invoice-yada',
      value: formatCurrency(finance.totalRevenue),
      change: getChangePercent(finance.totalRevenue, previousFinance.totalRevenue),
      tone: 'from-cyan-400/20 to-blue-500/10'
    },
    {
      label: 'Kharashka',
      hint: 'Kharashaadka la diiwaan geliyay',
      value: formatCurrency(finance.totalExpenses),
      change: getChangePercent(finance.totalExpenses, previousFinance.totalExpenses),
      tone: 'from-rose-400/20 to-orange-500/10'
    },
    {
      label: 'Faa\'iido Saafi',
      hint: 'Dakhli − Kharash',
      value: formatCurrency(finance.netProfit),
      change: getChangePercent(finance.netProfit, previousFinance.netProfit),
      tone: finance.netProfit >= 0 ? 'from-emerald-400/20 to-teal-500/10' : 'from-rose-500/20 to-red-600/10'
    }
  ];

  const snapshotCards = useMemo(
    () => buildSnapshotCards(range, chartPoints),
    [range, chartPoints]
  );

  const topExpenses = useMemo(() => {
    const map = {};
    periodExpenses.forEach((exp) => {
      const key = exp.category || 'kale';
      map[key] = (map[key] || 0) + toMoney(exp.amount);
    });
    return Object.entries(map)
      .map(([key, value]) => ({
        key,
        label: EXPENSE_CATEGORIES.find((c) => c.value === key)?.label || key,
        value
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 4);
  }, [periodExpenses]);

  return (
    <div className="space-y-5 pb-4">
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-red-900 to-rose-700 text-white shadow-2xl">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,#fff,transparent_28%),radial-gradient(circle_at_80%_0%,#fbbf24,transparent_32%)]" />
        <div className="relative p-5 md:p-7">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-red-100/80">Sahalguur Control</p>
              <h1 className="text-2xl md:text-4xl font-black mt-1">Dashboard-ka Maareeyaha</h1>
              <p className="text-red-50/80 mt-2 flex items-center gap-2 text-sm">
                <FiCalendar /> {periodLabel}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-1 flex">
                {PERIODS.map((period) => (
                  <button
                    key={period.id}
                    type="button"
                    onClick={() => {
                      setRange(period.id);
                      setAnchor(new Date());
                    }}
                    className={`px-3 py-2 rounded-xl text-sm font-semibold transition ${
                      range === period.id ? 'bg-white text-red-700 shadow' : 'text-white/80 hover:bg-white/10'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center bg-white/10 backdrop-blur rounded-2xl">
                <button
                  type="button"
                  onClick={() => setAnchor((prev) => shiftAnchor(range, prev, -1))}
                  className="p-3 hover:bg-white/10 rounded-l-2xl"
                  aria-label="Horay"
                >
                  <FiChevronLeft />
                </button>
                <button
                  type="button"
                  onClick={() => setAnchor(new Date())}
                  className="px-3 text-xs font-semibold uppercase tracking-wide"
                >
                  Hadda
                </button>
                <button
                  type="button"
                  onClick={() => setAnchor((prev) => shiftAnchor(range, prev, 1))}
                  className="p-3 hover:bg-white/10 rounded-r-2xl"
                  aria-label="Xiga"
                >
                  <FiChevronRight />
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
            {kpis.map((kpi) => (
              <div key={kpi.label} className={`rounded-2xl bg-gradient-to-br ${kpi.tone} border border-white/15 p-4 backdrop-blur-sm`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-white/70">{kpi.label}</p>
                  <Trend value={kpi.change} />
                </div>
                <p className="text-2xl md:text-3xl font-black mt-1">{kpi.value}</p>
                <p className="text-[11px] text-white/60 mt-1">{kpi.hint}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Quatations', value: periodQuotations.length, sub: `${pendingCount} sugaya`, icon: FiClock, onClick: () => navigate('/admin/quotations') },
          { label: 'Invoices', value: periodInvoices.length, sub: `Celcelis ${formatCurrency(avgInvoice)}`, icon: FiFileText, onClick: () => navigate('/admin/invoices') },
          { label: 'Macaamiil', value: customers, sub: 'Kuwa xilligan', icon: FiUsers },
          { label: 'Shaqaale', value: employees.length, sub: 'Kooxda shaqada', icon: FiTrendingUp, onClick: () => navigate('/admin/employees') }
        ].map((card) => (
          <button
            key={card.label}
            type="button"
            onClick={card.onClick}
            className="text-left bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition"
          >
            <div className="w-9 h-9 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mb-3">
              <card.icon />
            </div>
            <p className="text-xs text-slate-500">{card.label}</p>
            <p className="text-2xl font-black text-slate-900">{card.value}</p>
            <p className="text-[11px] text-slate-400 mt-1">{card.sub}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 bg-white rounded-3xl border border-slate-100 shadow-sm p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-bold text-slate-900">Dakhli vs Kharash</h2>
              <p className="text-xs text-slate-500">
                {range === 'month' ? 'Maalmaha bishan (invoice-yada)' : 'Bil kasta sanadkan (invoice-yada)'}
              </p>
            </div>
          </div>
          <div className="h-72">
            {chartPoints.some((p) => p.dakhli || p.kharash) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartPoints}>
                  <defs>
                    <linearGradient id="dakhliFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="kharashFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#e11d48" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#e11d48" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<ChartTooltip />} />
                  <Area type="monotone" dataKey="dakhli" name="Dakhli" stroke="#0284c7" fill="url(#dakhliFill)" strokeWidth={2.5} />
                  <Area type="monotone" dataKey="kharash" name="Kharash" stroke="#e11d48" fill="url(#kharashFill)" strokeWidth={2.5} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Xilligan xog lama helin
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 md:p-5">
          <h2 className="font-bold text-slate-900">Adeegyada</h2>
          <p className="text-xs text-slate-500 mb-4">Qaybinta dakhliga</p>
          <div className="h-56">
            {periodInvoices.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    { name: 'Guryaha', value: periodInvoices.filter((i) => i.serviceType === 'gurista-guryaha').reduce((s, i) => s + getInvoiceRevenue(i), 0) },
                    { name: 'Xafiis', value: periodInvoices.filter((i) => i.serviceType === 'gurista-xafiisyada').reduce((s, i) => s + getInvoiceRevenue(i), 0) },
                    { name: 'Ganacsi', value: periodInvoices.filter((i) => i.serviceType === 'gurista-ganacsiyada').reduce((s, i) => s + getInvoiceRevenue(i), 0) }
                  ]}
                  layout="vertical"
                >
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#be123c" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                Invoice lama jiro
              </div>
            )}
          </div>
          <div className="mt-3 space-y-2">
            {topExpenses.length === 0 ? (
              <p className="text-xs text-slate-400">Kharash lama jiro xilligan</p>
            ) : (
              topExpenses.map((item) => (
                <div key={item.key} className="flex justify-between text-sm">
                  <span className="text-slate-500">{item.label}</span>
                  <span className="font-semibold text-rose-600">{formatCurrency(item.value)}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4 md:p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-bold text-slate-900">
              {range === 'month' ? 'Toddobaadyada bishan' : 'Bilaha sanadka'}
            </h2>
            <p className="text-xs text-slate-500">Riix si aad u eegto xilligaas</p>
          </div>
        </div>
        <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
          {snapshotCards.map((card) => (
            <button
              key={card.key}
              type="button"
              onClick={() => {
                if (range === 'year' && card.date) {
                  setRange('month');
                  setAnchor(card.date);
                } else if (range === 'month' && card.date) {
                  setAnchor(card.date);
                }
              }}
              className="rounded-2xl border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-md p-3 text-left transition"
            >
              <p className="text-xs font-semibold text-slate-500">{card.fullLabel || card.label}</p>
              <p className="text-lg font-black text-sky-700 mt-1">{formatCurrency(card.dakhli)}</p>
              <p className="text-[11px] text-rose-500">Kharash {formatCurrency(card.kharash)}</p>
              <p className={`text-[11px] font-semibold ${card.faaido >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                Faa'iido {formatCurrency(card.faaido)}
              </p>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4">
          <h3 className="font-bold text-slate-900 mb-3">Quatations</h3>
          {loadingQuotations ? (
            <div className="py-8 flex justify-center">
              <div className="w-6 h-6 border-2 border-slate-200 border-t-red-600 rounded-full animate-spin" />
            </div>
          ) : periodQuotations.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Xilligan lama helin</p>
          ) : (
            <div className="space-y-2">
              {periodQuotations.slice(0, 5).map((q) => (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => navigate(`/admin/quotations/${q.id}`)}
                  className="w-full flex items-center justify-between p-2.5 rounded-xl bg-slate-50 hover:bg-red-50 text-left"
                >
                  <div>
                    <p className="font-medium text-sm">{q.customerName}</p>
                    <p className="text-xs text-slate-500">{getServiceTypeLabel(q.serviceType)}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusBadgeClass(q.status)}`}>
                    {getStatusLabel(q.status)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4">
          <h3 className="font-bold text-slate-900 mb-3">Invoices</h3>
          {periodInvoices.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Xilligan lama helin</p>
          ) : (
            <div className="space-y-2">
              {periodInvoices.slice(0, 5).map((inv) => (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                  className="w-full flex items-center justify-between p-2.5 rounded-xl bg-slate-50 hover:bg-sky-50 text-left"
                >
                  <div>
                    <p className="font-mono text-xs font-semibold">{inv.invoiceNumber}</p>
                    <p className="text-xs text-slate-500">{inv.customerName}</p>
                  </div>
                  <span className="font-bold text-sky-700 text-sm">{formatCurrency(getInvoiceRevenue(inv))}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-4">
          <h3 className="font-bold text-slate-900 mb-3">Kharashyada</h3>
          {periodExpenses.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Xilligan lama helin</p>
          ) : (
            <div className="space-y-2">
              {periodExpenses.slice(0, 5).map((exp) => (
                <div key={exp.id} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50">
                  <div>
                    <p className="font-medium text-sm">
                      {EXPENSE_CATEGORIES.find((c) => c.value === exp.category)?.label || exp.category}
                    </p>
                    <p className="text-xs text-slate-500">{exp.employeeName || exp.description || '-'}</p>
                  </div>
                  <span className="font-bold text-rose-600 text-sm">-{formatCurrency(exp.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
